import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, Hash, Calculator, Trophy, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProgressDashboard = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const response = await fetch(`${API}/progress/summary`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setProgress(data.progress);
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, []);

  const modules = [
    {
      id: 'abcd',
      name: 'ABCD',
      icon: <BookOpen className="w-8 h-8" />,
      color: 'from-spark to-pink-400',
      path: '/learn/abcd'
    },
    {
      id: 'counting',
      name: 'Counting',
      icon: <Hash className="w-8 h-8" />,
      color: 'from-teal to-aqua',
      path: '/learn/counting'
    },
    {
      id: 'tables',
      name: 'Tables',
      icon: <Calculator className="w-8 h-8" />,
      color: 'from-seagreen to-teal',
      path: '/learn/tables'
    }
  ];

  const getProgressData = (moduleId) => {
    if (!progress || !progress[moduleId]) {
      return { mastered: 0, total: 26, percentage: 0 };
    }
    const data = progress[moduleId];
    return {
      ...data,
      percentage: Math.round((data.mastered / data.total) * 100)
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-seagreen border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative z-10 pb-8">
      {/* Back Button */}
      <Button
        data-testid="back-btn"
        variant="ghost"
        onClick={() => navigate('/')}
        className="back-button bg-white/80 backdrop-blur-sm"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Home
      </Button>

      {/* Header */}
      <div className="pt-20 pb-8 px-6 text-center">
        <div className="flex items-center justify-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full border-4 border-seagreen bg-gradient-to-br from-seagreen to-teal flex items-center justify-center">
            <span className="text-2xl">🎓</span>
          </div>
          <div className="text-left">
            <h1 className="text-3xl font-heading text-foreground">My Progress</h1>
            <p className="text-muted-foreground">Keep learning, keep growing!</p>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-4xl px-6">
        {/* Progress Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {modules.map((module, index) => {
            const data = getProgressData(module.id);
            
            return (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-3xl p-6 shadow-card"
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${module.color} flex items-center justify-center text-white mb-4`}>
                  {module.icon}
                </div>
                
                <h3 className="text-xl font-heading text-foreground mb-2">{module.name}</h3>
                
                {/* Progress Circle */}
                <div className="progress-circle mx-auto mb-4">
                  <svg viewBox="0 0 100 100" className="w-full h-full">
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="#E2E8F0"
                      strokeWidth="8"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="45"
                      fill="none"
                      stroke="#2E8B57"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${data.percentage * 2.83} 283`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="progress-circle-text">
                    <span className="text-2xl font-heading text-seagreen">{data.percentage}%</span>
                    <span className="text-xs text-muted-foreground">{data.mastered}/{data.total}</span>
                  </div>
                </div>

                <Button
                  data-testid={`continue-${module.id}-btn`}
                  onClick={() => navigate(module.path)}
                  className="w-full btn-playful btn-toby text-sm"
                >
                  Continue Learning
                </Button>
              </motion.div>
            );
          })}
        </div>

        {/* Achievements Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-3xl p-6 shadow-card"
        >
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-6 h-6 text-sunshine" />
            <h3 className="text-xl font-heading text-foreground">Achievements</h3>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'First Letter', emoji: '🅰️', unlocked: (progress?.abcd?.mastered || 0) >= 1 },
              { name: 'ABC Master', emoji: '📚', unlocked: (progress?.abcd?.mastered || 0) >= 10 },
              { name: 'Counter', emoji: '🔢', unlocked: (progress?.counting?.mastered || 0) >= 5 },
              { name: 'Math Wizard', emoji: '🧙', unlocked: (progress?.tables?.mastered || 0) >= 5 },
            ].map((achievement, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-2xl text-center transition-all ${
                  achievement.unlocked 
                    ? 'bg-gradient-to-br from-sunshine/20 to-coral/20' 
                    : 'bg-muted/30 opacity-50'
                }`}
              >
                <span className="text-3xl">{achievement.emoji}</span>
                <p className="text-sm font-medium mt-2">{achievement.name}</p>
                {achievement.unlocked && <Star className="w-4 h-4 text-sunshine mx-auto mt-1" />}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProgressDashboard;
